import { nextTick } from 'vue'

import { useNotificationStore } from '@/stores/notification'

import { extractApiError } from '@/utils/api'

export const SPA_VERSION_KEY = 'spa-version'

export interface UseVersionReturnValue {
  /**
   * Checks that the SPA version has not changed.
   * If it has then an alert will appear to force them to refresh.
   */
  checkSpaVersion: (version: string | undefined) => Promise<void>
  /**
   * Checks if the provided error is due to a PolicyState application concurrency error.
   * If so, displays an alert forcing page refresh.
   */
  checkApplicationError: (error: unknown) => boolean
}

export function useVersion (): UseVersionReturnValue {
  const notificationStore = useNotificationStore()
  let notificationDialog = false
  const checkSpaVersion: UseVersionReturnValue['checkSpaVersion'] = async (version) => {
    if (!version) return
    const spaVersions = ((): string[] | null => {
      try {
        const raw = sessionStorage.getItem(SPA_VERSION_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : null
      } catch {
        return null
      }
    })()

    if (!spaVersions) {
      sessionStorage.setItem(SPA_VERSION_KEY, JSON.stringify([version]))
      return
    }

    if (spaVersions.includes(version)) return
    sessionStorage.setItem(SPA_VERSION_KEY, JSON.stringify([...spaVersions, version]))

    if (notificationDialog) return
    notificationDialog = true
    if (await notificationStore.confirm({
      title: 'PolicyFly has been updated',
      body: 'Please click Confirm to reload the page.',
      persistent: true,
      onlyConfirm: import.meta.env.VITE_DISMISS_UPDATE !== 'true',
    })) {
      location.reload()
    }
  }

  const checkApplicationError: UseVersionReturnValue['checkApplicationError'] = (error) => {
    if (extractApiError(error).includes('resource version conflict')) {
      nextTick(async () => {
        await notificationStore.confirm({
          title: 'Application has been updated',
          body: 'Another user has made changes to this Policy. Please refresh the page to see the latest version.',
          persistent: true,
          onlyConfirm: true,
        })
        location.reload()
      })
      return true
    }
    return false
  }

  return {
    checkApplicationError,
    checkSpaVersion,
  }
}
